export default async function useApi(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  body?: any,
  isForm?: boolean,
  tenantId?: string,
  hasNoAuth?: boolean,
  expectBinary?: boolean,
  bypassCache?: boolean
) {
  const { apiBaseUrl } = useAppConfig()
  const { user, contact, logout } = useUserStore()
  const isContactUrl = url.startsWith('css/') || url.startsWith('public/')

  try {
    // Log the request for debugging
    console.log(`[useApi] Making ${method} request to:`, `${apiBaseUrl}${url}`)

    // Use native fetch instead of $fetch to allow service worker interception
    const response = await fetch(`${apiBaseUrl}${url}`, {
      method,
      headers: {
        accept: expectBinary ? 'application/pdf' : 'application/json',
        'X-Tenant': `${tenantId || (isContactUrl ? contact.tenantId : user.tenant_identifier)}`,
        ...(hasNoAuth
          ? {}
          : { Authorization: `Bearer ${isContactUrl ? contact.token : user.api_token}` }),
        ...(body && !isForm ? { 'Content-Type': 'application/json' } : {})
      },
      body: isForm ? body : body ? JSON.stringify(body) : undefined,
      // Force fresh data from network when online and bypassCache is true
      cache: bypassCache && navigator.onLine ? 'no-cache' : 'default'
    })

    // Handle non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        statusCode: response.status,
        statusMessage: response.statusText,
        data: { message: response.statusText }
      }))

      // Throw error with same structure as $fetch
      const error: any = new Error(errorData.data?.message || response.statusText)
      error.statusCode = response.status
      error.data = errorData.data || errorData
      throw error
    }

    // Return binary data as ArrayBuffer if expectBinary is true
    if (expectBinary) {
      return await response.arrayBuffer()
    }

    // Return parsed JSON response
    const data = await response.json().catch(() => ({}))
    return data
  } catch (error: any) {
    // Extract error details
    const errorData = error
    const errorFields = errorData?.data?.errors || { message: [errorData?.data?.message ?? ''] }

    // Display error details to the user
    Object.keys(errorFields).forEach((field) => {
      toast.value?.add({
        severity: 'error',
        detail: errorFields?.[field]?.[0] || 'Unknown error',
        life: TOAST_LIFE_TIME_MS
      })
    })
    if (error.statusCode === 401) {
      logout()
      return {}
    }
  }
}
